import axiosInstance from "../../interceptor/axiosInstance";
import ApiRoutes from "../../routes/routeConstants/apiRoutes";
import { UserRoleEnum } from "../../enums/userRole.enum";

export interface CaseChatAttachmentPayload {
  url: string;
  filename: string;
  content_type: string;
  byte_size: number;
}

export interface CaseChatMessagePayload {
  id: number;
  body: string;
  created_at: string;
  sender: {
    type: string;
    id: number;
    name: string;
  };
  attachment?: CaseChatAttachmentPayload | null;
}

function chatPath(role: UserRoleEnum, caseDetailId: number): string {
  const id = String(caseDetailId);
  switch (role) {
    case UserRoleEnum.ADMIN:
      return ApiRoutes.ADMIN_CASE_CHAT_MESSAGES.replace(":caseDetailId", id);
    case UserRoleEnum.DOCTOR:
      return ApiRoutes.DOCTOR_CASE_CHAT_MESSAGES.replace(":caseDetailId", id);
    case UserRoleEnum.SCAN_CENTER:
      return ApiRoutes.SCAN_CENTER_CASE_CHAT_MESSAGES.replace(":caseDetailId", id);
    default:
      throw new Error("Case chat is not available for this role");
  }
}

export default class CaseChatService {
  static fetchMessages(
    caseDetailId: number,
    role: UserRoleEnum,
    onSuccess: (messages: CaseChatMessagePayload[]) => void,
    onError: () => void,
    onFinal: () => void
  ) {
    axiosInstance
      .get(chatPath(role, caseDetailId))
      .then((res: any) => {
        const list = res?.data?.messages;
        onSuccess(Array.isArray(list) ? list : []);
      })
      .catch(() => onError())
      .finally(() => onFinal());
  }

  static markRead(
    caseDetailId: number,
    role: UserRoleEnum,
    onSuccess: () => void,
    onError: () => void,
    onFinal: () => void
  ) {
    axiosInstance
      .post(`${chatPath(role, caseDetailId)}/mark_read`, {})
      .then(() => onSuccess())
      .catch(() => onError())
      .finally(() => onFinal());
  }

  static sendMessage(
    caseDetailId: number,
    role: UserRoleEnum,
    body: string,
    file: File | null,
    onSuccess: (message: CaseChatMessagePayload) => void,
    onError: (serverMessage?: string) => void,
    onFinal: () => void
  ) {
    const url = chatPath(role, caseDetailId);
    const text = body.trim();

    const parseErrorMessage = (err: unknown): string | undefined => {
      const data = (err as { response?: { data?: unknown } })?.response?.data as
        | { errors?: string[]; error?: string }
        | undefined;
      if (!data) return undefined;
      if (Array.isArray(data.errors) && data.errors.length > 0) return data.errors[0];
      if (typeof data.error === "string") return data.error;
      return undefined;
    };

    if (file) {
      const formData = new FormData();
      formData.append("case_chat_message[body]", text);
      formData.append("case_chat_message[attachment]", file);
      axiosInstance
        .post(url, formData)
        .then((res: any) => {
          const msg = res?.data?.message;
          if (msg) onSuccess(msg);
          else onError();
        })
        .catch((err) => onError(parseErrorMessage(err)))
        .finally(() => onFinal());
    } else {
      axiosInstance
        .post(url, {
          case_chat_message: { body: text },
        })
        .then((res: any) => {
          const msg = res?.data?.message;
          if (msg) onSuccess(msg);
          else onError();
        })
        .catch((err) => onError(parseErrorMessage(err)))
        .finally(() => onFinal());
    }
  }
}
